import React, { useEffect, useState } from 'react';
import { TrendUp, TrendDown } from '@phosphor-icons/react';
import { api } from '../api';

/**
 * Bloomberg-style scrolling ticker tape with live indices.
 * Data comes from the same cached endpoints the dashboard uses (24h server
 * cache), so this adds no extra load on the market-data providers.
 * Pauses on hover; static under prefers-reduced-motion.
 */
export default function MarketTicker() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([api.getIndices(), api.getVix()])
      .then(([idx, vix]) => {
        if (!mounted) return;
        const list = [];
        if (idx.status === 'fulfilled' && Array.isArray(idx.value)) {
          for (const it of idx.value) {
            if (it && it.price != null) list.push({ label: it.name || it.symbol, price: it.price, chg: it.change_pct });
          }
        }
        if (vix.status === 'fulfilled' && vix.value?.price != null) {
          list.push({ label: 'VIX', price: vix.value.price, chg: vix.value.change_pct });
        }
        setItems(list);
      })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Reserve the strip's height (37px = what the live ticker measures) while the
  // data loads, so it doesn't push the hero down when it appears. If there is no
  // data at all, collapse.
  if (!items.length) {
    return loading ? <div className="h-[37px] border-b border-line" aria-hidden="true" /> : null;
  }

  const Entry = ({ it }) => {
    const up = (it.chg ?? 0) >= 0;
    return (
      <span className="inline-flex items-center gap-2 px-5 whitespace-nowrap">
        <span className="text-text-muted text-[11px] font-bold uppercase tracking-wider">{it.label}</span>
        <span className="font-mono text-xs text-text-primary font-semibold">
          {Number(it.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        {it.chg != null && (
          <span className={`inline-flex items-center gap-0.5 font-mono text-[11px] font-bold ${up ? 'text-accent-green' : 'text-accent-red'}`}>
            {up ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
            {up ? '+' : ''}{Number(it.chg).toFixed(2)}%
          </span>
        )}
        <span className="text-white/10 select-none">·</span>
      </span>
    );
  };

  return (
    <div
      className="ticker-wrap relative overflow-hidden border-b border-line bg-surface/40 backdrop-blur-sm py-1.5 h-[37px]"
      data-testid="market-ticker"
      aria-hidden="true"
    >
      {/* Edge fades */}
      <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-bg to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-bg to-transparent z-10 pointer-events-none" />
      <div className="ticker-track">
        {/* Content duplicated once so the -50% translate loops seamlessly */}
        {[0, 1].map((dup) => (
          <div key={dup} className="flex items-center">
            {items.map((it, i) => <Entry key={`${dup}-${i}`} it={it} />)}
          </div>
        ))}
      </div>
    </div>
  );
}
